import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Key, FileText, Copy, ChevronLeft, Eye, EyeOff, Trash2, ToggleLeft, ToggleRight, Link, Code, ExternalLink, Edit2, MoreVertical } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl, API_BASE_URL } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Badge, Card, Modal, ModalHeader, ModalBody, ModalFooter, EmptyState, MultiSelect } from '../../../components/ui'
import { inputClass } from '../../../styles/forms'

interface ApiKey {
  id: number; name: string; api_key: string; environment: string;
  rate_limit: number; last_used_at: string | null; is_active: boolean;
}

interface CaptureForm {
  id: number; name: string; description: string | null; slug: string;
  require_name: boolean; require_phone: boolean; require_email: boolean;
  require_cpf: boolean; require_address: boolean; require_city: boolean;
  require_full_address: boolean; require_birth_date: boolean;
  require_voter_reg: boolean; auto_add_campaign: boolean; is_active: boolean;
  submissions_count: number; last_submission_at: string | null; channels: string[];
  categories: string[];
}

interface Channel { id: number; code: string; label: string }
interface VoterCategory { id: number; name: string; children: VoterCategory[] }

function flattenCategories(cats: VoterCategory[], level = 0): { id: number; name: string; level: number }[] {
  const result: { id: number; name: string; level: number }[] = []
  for (const cat of cats) {
    result.push({ id: cat.id, name: cat.name, level })
    result.push(...flattenCategories(cat.children, level + 1))
  }
  return result
}

export default function IntegrationsPage() {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [forms, setForms] = useState<CaptureForm[]>([])
  const [channels, setChannels] = useState<Channel[]>([])
  const [voterCategories, setVoterCategories] = useState<VoterCategory[]>([])
  const [keyModalOpen, setKeyModalOpen] = useState(false)
  const [formModalOpen, setFormModalOpen] = useState(false)
  const [editingFormId, setEditingFormId] = useState<number | null>(null)
  const [showKeys, setShowKeys] = useState<Record<number, boolean>>({})
  const [keyName, setKeyName] = useState('')
  const [docsForm, setDocsForm] = useState<CaptureForm | null>(null)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    name: '', description: '',
    require_name: true, require_phone: true, require_email: false,
    require_cpf: false, require_city: false, require_full_address: false, require_birth_date: false,
    require_voter_reg: false, auto_add_campaign: true, channel_ids: [] as number[],
    category_ids: [] as number[],
  })

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    try {
      const [k, f, c, cats] = await Promise.all([
        axios.get(buildApiUrl('/v1/integrations/keys')),
        axios.get(buildApiUrl('/v1/integrations/forms')),
        axios.get(buildApiUrl('/v1/voters/channels')),
        axios.get(buildApiUrl('/v1/voter-categories')),
      ])
      setKeys(k.data.data)
      setForms(f.data.data)
      setChannels(c.data.data)
      setVoterCategories(cats.data.data)
    } catch { /* */ } finally { setLoading(false) }
  }

  async function createKey() {
    if (!keyName || saving) return
    setSaving(true)
    try {
      const res = await axios.post(buildApiUrl('/v1/integrations/keys'), { name: keyName })
      toast('success', 'Chave criada', `${res.data.data.api_key.substring(0, 20)}...`)
      setKeyModalOpen(false)
      setKeyName('')
      fetchAll()
    } catch { toast('error', 'Erro ao criar chave') } finally { setSaving(false) }
  }

  async function toggleKey(id: number) {
    await axios.put(buildApiUrl(`/v1/integrations/keys/${id}/toggle`))
    fetchAll()
  }

  async function deleteKey(id: number) {
    await axios.delete(buildApiUrl(`/v1/integrations/keys/${id}`))
    toast('success', 'Chave excluída')
    fetchAll()
  }

  function openEditForm(f: CaptureForm) {
    setEditingFormId(f.id)
    // Buscar channel_ids e category_ids do formulário
    const channelIds = channels.filter(c => f.channels.includes(c.label)).map(c => c.id)
    const categoryIds = flattenCategories(voterCategories).filter(c => f.categories?.includes(c.name)).map(c => c.id)
    setFormData({
      name: f.name, description: f.description || '',
      require_name: f.require_name, require_phone: f.require_phone,
      require_email: f.require_email, require_cpf: f.require_cpf,
      require_city: f.require_city, require_full_address: f.require_full_address,
      require_birth_date: f.require_birth_date, require_voter_reg: f.require_voter_reg,
      auto_add_campaign: f.auto_add_campaign,
      channel_ids: channelIds, category_ids: categoryIds,
    })
    setFormModalOpen(true)
  }

  async function saveForm() {
    if (!formData.name || saving) return
    setSaving(true)
    try {
      if (editingFormId) {
        await axios.put(buildApiUrl(`/v1/integrations/forms/${editingFormId}`), formData)
        toast('success', 'Formulário atualizado')
      } else {
        await axios.post(buildApiUrl('/v1/integrations/forms'), formData)
        toast('success', 'Formulário criado')
      }
      setFormModalOpen(false)
      setEditingFormId(null)
      setFormData({ name: '', description: '', require_name: true, require_phone: true, require_email: false, require_cpf: false, require_city: false, require_full_address: false, require_birth_date: false, require_voter_reg: false, auto_add_campaign: true, channel_ids: [], category_ids: [] })
      fetchAll()
    } catch { toast('error', editingFormId ? 'Erro ao atualizar' : 'Erro ao criar formulário') } finally { setSaving(false) }
  }

  async function toggleForm(id: number) {
    await axios.put(buildApiUrl(`/v1/integrations/forms/${id}/toggle`))
    fetchAll()
  }

  function copyToClipboard(text: string) {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text)
    } else {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    toast('success', 'Copiado!')
  }

  function toggleChannelId(id: number) {
    setFormData(f => ({
      ...f,
      channel_ids: f.channel_ids.includes(id) ? f.channel_ids.filter(c => c !== id) : [...f.channel_ids, id],
    }))
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/settings/general')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Integrações</h1>
            <p className="text-gray-500 mt-1">API Keys, formulários de captação e logs</p>
          </div>
        </div>
        <Button variant="outline" size="sm" icon={<ExternalLink className="w-4 h-4" />} onClick={() => navigate('/settings/integrations/docs')}>
          Documentação
        </Button>
      </div>

      {/* API Keys */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Chaves de API</h2>
          <Button size="sm" icon={<Plus className="w-4 h-4" />} onClick={() => { setKeyName(''); setKeyModalOpen(true) }}>Nova Chave</Button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1].map(i => (
              <Card key={i} padding="md">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-xl animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-40 bg-gray-100 rounded-lg animate-pulse" />
                    <div className="h-3 w-64 bg-gray-50 rounded-lg animate-pulse" />
                  </div>
                  <div className="flex gap-1">
                    <div className="h-8 w-8 bg-gray-50 rounded-lg animate-pulse" />
                    <div className="h-8 w-8 bg-gray-50 rounded-lg animate-pulse" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : keys.length === 0 ? (
          <Card>
            <EmptyState icon={<Key className="w-6 h-6" />} title="Nenhuma chave de API" description="Crie uma chave para integrar com sistemas externos." />
          </Card>
        ) : (
          <div className="space-y-3">
            {keys.map(k => (
              <Card key={k.id} padding="md">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Key className="w-4 h-4 text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-medium text-gray-900">{k.name}</p>
                        <Badge variant={k.is_active ? 'success' : 'gray'} dot size="sm">{k.is_active ? 'Ativa' : 'Inativa'}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-gray-400 font-mono">
                          {showKeys[k.id] ? k.api_key : k.api_key.substring(0, 12) + '••••••••••••'}
                        </code>
                        <button onClick={() => setShowKeys(s => ({ ...s, [k.id]: !s[k.id] }))} className="text-gray-400 hover:text-gray-600">
                          {showKeys[k.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                        <button onClick={() => copyToClipboard(k.api_key)} className="text-gray-400 hover:text-gray-600">
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggleKey(k.id)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                      {k.is_active ? <ToggleRight className="w-5 h-5 text-success-500" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                    <button onClick={() => deleteKey(k.id)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-error-500 hover:bg-error-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Formulários de captação */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Formulários de captação</h2>
          <Button size="sm" icon={<Plus className="w-4 h-4" />} onClick={() => { setEditingFormId(null); setFormData({ name: '', description: '', require_name: true, require_phone: true, require_email: false, require_cpf: false, require_city: false, require_full_address: false, require_birth_date: false, require_voter_reg: false, auto_add_campaign: true, channel_ids: [], category_ids: [] }); setFormModalOpen(true) }}>Novo Formulário</Button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Card key={i} padding="md">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2.5">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-44 bg-gray-100 rounded-lg animate-pulse" />
                      <div className="h-5 w-12 bg-gray-50 rounded-full animate-pulse" />
                    </div>
                    <div className="flex gap-1.5">
                      <div className="h-5 w-16 bg-gray-50 rounded-full animate-pulse" />
                      <div className="h-5 w-20 bg-gray-50 rounded-full animate-pulse" />
                    </div>
                    <div className="h-3 w-72 bg-gray-50 rounded-lg animate-pulse" />
                  </div>
                  <div className="h-8 w-8 bg-gray-50 rounded-lg animate-pulse" />
                </div>
              </Card>
            ))}
          </div>
        ) : forms.length === 0 ? (
          <Card>
            <EmptyState icon={<FileText className="w-6 h-6" />} title="Nenhum formulário" description="Crie formulários para captar contatos pelo site, WhatsApp e outros canais." />
          </Card>
        ) : (
          <div className="space-y-3">
            {forms.map(f => (
              <Card key={f.id} padding="md">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-gray-900">{f.name}</p>
                      <Badge variant={f.is_active ? 'success' : 'gray'} dot size="sm">{f.is_active ? 'Ativo' : 'Inativo'}</Badge>
                    </div>
                    {f.channels.length > 0 && (
                      <div className="flex items-center gap-1.5 mb-2">
                        {f.channels.map(ch => (
                          <span key={ch} className="text-[11px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">{ch}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span>Campos: {[f.require_name && 'Nome', f.require_phone && 'Telefone', f.require_email && 'Email', f.require_cpf && 'CPF'].filter(Boolean).join(', ')}</span>
                      <span>{f.submissions_count} envio{f.submissions_count !== 1 ? 's' : ''}</span>
                      {f.categories?.length > 0 && <span>Categorias: {f.categories.join(', ')}</span>}
                    </div>
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === f.id ? null : f.id)}
                      className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {openMenuId === f.id && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
                        <div className="absolute right-0 top-9 z-20 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1">
                          <button
                            onClick={() => { setOpenMenuId(null); openEditForm(f) }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Edit2 className="w-3.5 h-3.5 text-gray-400" /> Editar
                          </button>
                          <button
                            onClick={() => { setOpenMenuId(null); toggleForm(f.id) }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            {f.is_active
                              ? <><ToggleLeft className="w-3.5 h-3.5 text-gray-400" /> Desativar</>
                              : <><ToggleRight className="w-3.5 h-3.5 text-success-500" /> Ativar</>
                            }
                          </button>
                          <button
                            onClick={() => { setOpenMenuId(null); setDocsForm(f) }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <ExternalLink className="w-3.5 h-3.5 text-gray-400" /> Documentação da API
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal nova chave */}
      <Modal open={keyModalOpen} onClose={() => setKeyModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setKeyModalOpen(false)}>Nova Chave de API</ModalHeader>
        <ModalBody>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome da chave</label>
            <input value={keyName} onChange={e => setKeyName(e.target.value)} placeholder="Ex: Chave do Site" className={inputClass} />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setKeyModalOpen(false)}>Cancelar</Button>
          <Button onClick={createKey} disabled={!keyName} loading={saving}>Criar Chave</Button>
        </ModalFooter>
      </Modal>

      {/* Modal novo formulário */}
      <Modal open={formModalOpen} onClose={() => setFormModalOpen(false)} size="lg">
        <ModalHeader onClose={() => setFormModalOpen(false)}>{editingFormId ? 'Editar Formulário' : 'Novo Formulário de Captação'}</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome do formulário</label>
              <input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Landing Page Campanha" className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Descrição</label>
              <input value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Para que serve este formulário" className={inputClass} />
            </div>

            {/* Categorias */}
            {voterCategories.length > 0 && (
              <MultiSelect
                label="Categorias"
                placeholder="Selecione as categorias..."
                options={flattenCategories(voterCategories).map(c => ({
                  value: String(c.id),
                  label: c.level > 0 ? '  '.repeat(c.level) + c.name : c.name,
                }))}
                selected={formData.category_ids.map(String)}
                onChange={ids => setFormData(f => ({ ...f, category_ids: ids.map(Number) }))}
              />
            )}

            {/* Canais */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Onde será utilizado</label>
              <div className="flex flex-wrap gap-2">
                {channels.filter(c => !['MANUAL', 'LEADER'].includes(c.code)).map(c => (
                  <button key={c.id} type="button" onClick={() => toggleChannelId(c.id)}
                    className={clsx('px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                      formData.channel_ids.includes(c.id)
                        ? 'bg-brand-50 border-brand-200 text-brand-600'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                    )}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Campos obrigatórios */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Campos obrigatórios</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'require_name', label: 'Nome', locked: true },
                  { key: 'require_phone', label: 'Telefone', locked: false },
                  { key: 'require_email', label: 'Email', locked: false },
                  { key: 'require_cpf', label: 'CPF', locked: false },
                  { key: 'require_city', label: 'Cidade', locked: false },
                  { key: 'require_full_address', label: 'Endereço completo', locked: false },
                  { key: 'require_birth_date', label: 'Data de nascimento', locked: false },
                  { key: 'require_voter_reg', label: 'Dados eleitorais', locked: false },
                ].map(field => (
                  <label key={field.key} className={clsx('flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-colors cursor-pointer',
                    (formData as any)[field.key] ? 'bg-brand-25 border-brand-100' : 'border-gray-200'
                  )}>
                    <input type="checkbox" checked={(formData as any)[field.key]} disabled={field.locked}
                      onChange={e => {
                        const update: any = { [field.key]: e.target.checked }
                        if (field.key === 'require_city' && e.target.checked) update.require_full_address = false
                        if (field.key === 'require_full_address' && e.target.checked) update.require_city = false
                        setFormData({ ...formData, ...update })
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 disabled:opacity-50" />
                    <span className="text-sm text-gray-700">{field.label}</span>
                    {field.locked && <span className="text-[10px] text-gray-400">(obrigatório)</span>}
                  </label>
                ))}
              </div>
            </div>

            {/* Auto campanha */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => setFormData({ ...formData, auto_add_campaign: !formData.auto_add_campaign })}>
              <div className={clsx('w-10 h-6 rounded-full transition-colors relative', formData.auto_add_campaign ? 'bg-brand-500' : 'bg-gray-300')}>
                <span className={clsx('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-xs transition-transform', formData.auto_add_campaign ? 'left-[18px]' : 'left-0.5')} />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Auto-engajar na campanha ativa</span>
                <p className="text-xs text-gray-400">Eleitores captados entram automaticamente na campanha</p>
              </div>
            </div>

          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setFormModalOpen(false)}>Cancelar</Button>
          <Button onClick={saveForm} disabled={!formData.name} loading={saving}>{editingFormId ? 'Salvar' : 'Criar Formulário'}</Button>
        </ModalFooter>
      </Modal>

      {/* Modal documentação do formulário */}
      <Modal open={!!docsForm} onClose={() => setDocsForm(null)} size="lg">
        <ModalHeader onClose={() => setDocsForm(null)}>Documentação: {docsForm?.name}</ModalHeader>
        <ModalBody>
          {docsForm && (
            <div className="space-y-6">
              {/* Consultar formulário */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Consultar dados do formulário</h4>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-white bg-brand-500 px-2 py-0.5 rounded">GET</span>
                  <code className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded break-all">{API_BASE_URL}/v1/public/forms/{docsForm.slug}</code>
                  <button onClick={() => copyToClipboard(`${API_BASE_URL}/v1/public/forms/${docsForm.slug}`)} className="text-gray-400 hover:text-brand-500">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-xs text-gray-500">Retorna os dados do formulário e da organização (sem autenticação).</p>
              </div>

              {/* Enviar dados */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Enviar dados do formulário</h4>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-white bg-success-500 px-2 py-0.5 rounded">POST</span>
                  <code className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded break-all">{API_BASE_URL}/v1/public/forms/{docsForm.slug}/submit</code>
                  <button onClick={() => copyToClipboard(`${API_BASE_URL}/v1/public/forms/${docsForm.slug}/submit`)} className="text-gray-400 hover:text-brand-500">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-3">Envia <code className="text-brand-600">Content-Type: application/json</code></p>
              </div>

              {/* Campos */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Campos</h4>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Campo</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Obrigatório</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-sm">
                      {[
                        { field: 'name', type: 'string', required: docsForm.require_name, desc: 'Nome completo' },
                        { field: 'phone', type: 'string', required: docsForm.require_phone, desc: 'Telefone (11 dígitos)' },
                        { field: 'email', type: 'string', required: docsForm.require_email, desc: 'Email' },
                        { field: 'cpf', type: 'string', required: docsForm.require_cpf, desc: 'CPF (11 dígitos)' },
                        ...(docsForm.require_city ? [
                          { field: 'geo_city_id', type: 'integer', required: true, desc: 'ID da cidade (via /public/geo)' },
                        ] : []),
                        ...(docsForm.require_full_address ? [
                          { field: 'zip_code', type: 'string', required: true, desc: 'CEP (8 dígitos)' },
                          { field: 'street', type: 'string', required: false, desc: 'Rua' },
                          { field: 'number', type: 'string', required: false, desc: 'Número' },
                          { field: 'neighborhood', type: 'string', required: false, desc: 'Bairro' },
                          { field: 'geo_city_id', type: 'integer', required: false, desc: 'ID da cidade' },
                        ] : []),
                        { field: 'birth_date', type: 'date', required: docsForm.require_birth_date, desc: 'Data nasc. (YYYY-MM-DD)' },
                        ...(docsForm.require_voter_reg ? [
                          { field: 'voter_registration', type: 'string', required: true, desc: 'Título de eleitor' },
                          { field: 'electoral_zone', type: 'string', required: false, desc: 'Zona eleitoral' },
                          { field: 'electoral_section', type: 'string', required: false, desc: 'Seção eleitoral' },
                        ] : []),
                        { field: 'nickname', type: 'string', required: false, desc: 'Apelido' },
                        { field: 'channel', type: 'string', required: false, desc: 'Canal de origem: SITE, WHATSAPP, INSTAGRAM, FACEBOOK, TIKTOK, API, OTHER' },
                        { field: 'gender', type: 'string', required: false, desc: 'M, F ou O' },
                      ].map(p => (
                        <tr key={p.field}>
                          <td className="px-3 py-2"><code className="text-xs text-brand-600 bg-brand-50 px-1.5 py-0.5 rounded">{p.field}</code></td>
                          <td className="px-3 py-2 text-xs text-gray-500">{p.type}</td>
                          <td className="px-3 py-2">
                            {p.required
                              ? <span className="text-xs font-medium text-error-500 bg-error-50 px-1.5 py-0.5 rounded-full">Sim</span>
                              : <span className="text-xs text-gray-400">Não</span>
                            }
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">{p.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Exemplo */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Exemplo de envio</h4>
                <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 text-xs overflow-x-auto leading-relaxed">
                  <code>{`curl -X POST ${API_BASE_URL}/v1/public/forms/${docsForm.slug}/submit \\
  -H "Content-Type: application/json" \\
  -d '{
${[
  docsForm.require_name ? '    "name": "João Silva"' : '',
  docsForm.require_phone ? '    "phone": "82999999999"' : '',
  docsForm.require_email ? '    "email": "joao@email.com"' : '',
  docsForm.require_cpf ? '    "cpf": "12345678901"' : '',
  docsForm.require_city ? '    "geo_city_id": 2839' : '',
  docsForm.require_full_address ? '    "zip_code": "57036410",\n    "street": "Rua Example",\n    "number": "123",\n    "neighborhood": "Centro",\n    "geo_city_id": 2839' : '',
  docsForm.require_birth_date ? '    "birth_date": "1990-01-15"' : '',
  docsForm.require_voter_reg ? '    "voter_registration": "0123456789"' : '',
  '    "channel": "SITE"',
].filter(Boolean).join(',\n')}
  }'`}</code>
                </pre>
              </div>

              {/* Respostas */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Respostas</h4>
                <div className="space-y-2 text-xs">
                  <div className="flex gap-2">
                    <span className="font-bold text-success-600 bg-success-50 px-1.5 py-0.5 rounded">201</span>
                    <span className="text-gray-600">Cadastro realizado com sucesso</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-bold text-warning-600 bg-warning-50 px-1.5 py-0.5 rounded">409</span>
                    <span className="text-gray-600">Contato já cadastrado (retorna voter_id)</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-bold text-error-600 bg-error-50 px-1.5 py-0.5 rounded">422</span>
                    <span className="text-gray-600">Campos obrigatórios não enviados</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDocsForm(null)}>Fechar</Button>
          <Button onClick={() => navigate('/settings/integrations/docs')}>Documentação completa</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
